import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import '../Styles/Login.css';
// import logo from '../Images/SAVI AI -1.png';
import logo from '../Images/SAVI AI full.png';
import { FiUser, FiMail, FiPhone, FiHash, FiChevronDown, FiX } from 'react-icons/fi';

const VendorLogin = () => {
  const [loginMethod, setLoginMethod] = useState('');
  const [loginValue, setLoginValue] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showVendorSelection, setShowVendorSelection] = useState(false);
  const [vendors, setVendors] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [otp, setOtp] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [vendorCode, setVendorCode] = useState('');
  const [otpError, setOtpError] = useState('');
  const [timeLeft, setTimeLeft] = useState(300); // 5 minutes in seconds
  const navigate = useNavigate();
  const loginValueInputRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    const savedTheme = localStorage.getItem('theme') || 'light';
    document.documentElement.setAttribute('data-theme', savedTheme);
  }, []);

  useEffect(() => {
    if (showOtpModal && timeLeft > 0) {
      timerRef.current = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            clearInterval(timerRef.current);
            setOtpError('OTP has expired. Please request a new one.');
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [showOtpModal]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleLoginMethodChange = (e) => {
    setLoginMethod(e.target.value);
    setLoginValue('');
    setTimeout(() => loginValueInputRef.current?.focus(), 50);
  };

  const handleLoginValueChange = (e) => {
    setLoginValue(e.target.value);
  };

  const handleSignup = () => {
    navigate('/register');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Map frontend login methods to backend login_type
      const loginTypeMap = {
        'vendorId': 'vendor_code',
        'email': 'email',
        'vendorPan': 'pan',
        'phoneNumber': 'phone'
      };

      const payload = {
        login_type: loginTypeMap[loginMethod],
        identifier: loginValue
      };

      const response = await fetch(process.env.REACT_APP_OCR_REGISTER_SEARCH_API, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.vendors && data.vendors.length > 0) {
          if (data.vendors.length === 1) {
            // Only one vendor found, proceed directly to login
            await handleVendorLogin(data.vendors[0].vendor_code, data.vendors[0].vendor_mail);
          } else {
            // Multiple vendors found, show selection modal
            setVendors(data.vendors);
            setShowVendorSelection(true);
          }
          setError('');
        } else {
          setError('No vendor found with the provided information.');
        }
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Vendor search failed. Please check your credentials.');
      }
    } catch (err) {
      setError('An error occurred. Please try again later.');
      console.error('Vendor search error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleVendorLogin = async (vendorCode, vendorEmail) => {
    setLoading(true);
    setError('');

    try {
      const payload = {
        vendor_code: vendorCode,
        email: vendorEmail
      };

      const response = await fetch(process.env.REACT_APP_OCR_REGISTER_LOGIN_API, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        const data = await response.json();
        setUserEmail(data.mail);
        setVendorCode(vendorCode);
        setTimeLeft(300); // Reset timer to 5 minutes
        setShowVendorSelection(false);
        setShowOtpModal(true);
        setError('');
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Vendor login failed. Please try again.');
      }
    } catch (err) {
      setError('An error occurred. Please try again later.');
      console.error('Vendor login error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleVendorSelect = (vendor) => {
    setSelectedVendor(vendor);
    handleVendorLogin(vendor.vendor_code, vendor.vendor_mail);
  };

  const closeVendorSelectionModal = () => {
    setShowVendorSelection(false);
    setVendors([]);
    setSelectedVendor(null);
  };

  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setOtpError('');

    try {
      const payload = {
        identifier: userEmail,
        otp: otp,
        vendor_code: vendorCode
      };

      const response = await fetch(process.env.REACT_APP_OCR_REGISTER_VERIFYOTP_API, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        const data = await response.json();
        localStorage.setItem('isAuthenticated', 'true');
        localStorage.setItem('userType', 'vendor');
        localStorage.setItem('userEmail', userEmail);
        if (data.token) {
          localStorage.setItem('token', data.token);
        }
        if (data.session_id) {
          localStorage.setItem('session_id', data.session_id);
        }
        if (selectedVendor?.vendor_name) {
          localStorage.setItem('vendorName', selectedVendor.vendor_name);
        }
        if (selectedVendor?.vendor_code) {
          localStorage.setItem('vendorCode', selectedVendor.vendor_code);
        }
        setShowOtpModal(false);
        navigate('/invoice-hub', {
          replace: true,
          state: { functionalArea: 'Finance', useCase: 'Vendor Invoice Automation' },
        });
      } else {
        const errorData = await response.json();
        setOtpError(errorData.message || 'Invalid OTP. Please try again.');
      }
    } catch (err) {
      setOtpError('An error occurred. Please try again later.');
      console.error('OTP verification error:', err);
    } finally {
      setLoading(false);
    }
  };

  const closeOtpModal = () => {
    setShowOtpModal(false);
    setOtp('');
    setOtpError('');
    setTimeLeft(300);
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
  };

  const getInputIcon = () => {
    switch (loginMethod) {
      case 'vendorId': return <FiHash className="login-input-icon" />;
      case 'email': return <FiMail className="login-input-icon" />;
      case 'phoneNumber': return <FiPhone className="login-input-icon" />;
      case 'vendorPan': return <FiUser className="login-input-icon" />;
      default: return <FiUser className="login-input-icon" />;
    }
  };

  const getInputPlaceholder = () => {
    switch (loginMethod) {
      // case 'vendorName': return 'Enter your vendor name';
      case 'vendorId': return 'Enter your vendor ID';
      case 'email': return 'Enter your email address';
      case 'phoneNumber': return 'Enter your phone number';
      case 'vendorPan': return 'Enter your PAN number';
      default: return 'Enter your credential';
    }
  };

  const getInputType = () => {
    switch (loginMethod) {
      case 'email': return 'email';
      case 'phoneNumber': return 'tel';
      default: return 'text';
    }
  };

  return (
    <div className="login-main-container">
      <header className="login-header">
        <div className="login-logo">
          <img src={logo} alt="SAVI AI" className="login-logo-image" />
        </div>
        {/* <button className="login-signup-button" onClick={handleSignup}>Sign Up</button> */}
      </header>
      <div className='login-form-bg'>
        <div className="login-form-container">
          <h1 className="login-form-title">
          Sign in to <span className="login-title-highlight">Savi</span>
          </h1>

          {/* <p style={{ color: '#555', fontSize: '14px', marginBottom: '30px' }}>
            Choose your sign-in method
          </p> */}

          <form onSubmit={handleSubmit}>
            <div className="login-method-tabs">
              <button 
                type="button"
                className={`method-tab ${loginMethod === 'vendorId' ? 'active' : ''}`}
                onClick={() => {
                  setLoginMethod('vendorId');
                  setLoginValue('');
                  setError('');
                }}
                disabled={loading}
              >
                <FiHash size={18} />
                <span>Vendor ID</span>
              </button>
              <button 
                type="button"
                className={`method-tab ${loginMethod === 'email' ? 'active' : ''}`}
                onClick={() => {
                  setLoginMethod('email');
                  setLoginValue('');
                  setError('');
                }}
                disabled={loading}
              >
                <FiMail size={18} />
                <span>Email</span>
              </button>
              <button 
                type="button"
                className={`method-tab ${loginMethod === 'vendorPan' ? 'active' : ''}`}
                onClick={() => {
                  setLoginMethod('vendorPan');
                  setLoginValue('');
                  setError('');
                }}
                disabled={loading}
              >
                <FiUser size={18} />
                <span>PAN</span>
              </button>
              <button 
                type="button"
                className={`method-tab ${loginMethod === 'phoneNumber' ? 'active' : ''}`}
                onClick={() => {
                  setLoginMethod('phoneNumber');
                  setLoginValue('');
                  setError('');
                }}
                disabled={loading}
              >
                <FiPhone size={18} />
                <span>Phone</span>
              </button>
            </div>

            {loginMethod && (
              <div className="login-input-group login-password-field">
                {getInputIcon()}
                <input
                  type={getInputType()}
                  value={loginValue}
                  onChange={handleLoginValueChange}
                  placeholder={getInputPlaceholder()}
                  className="login-input-field"
                  ref={loginValueInputRef}
                  disabled={loading}
                />
              </div>
            )}

            {loginMethod && loginValue && (
              <div style={{ marginTop: 8 }}>
                <button
                  type="submit"
                  className="login-button"
                  disabled={loading || !loginValue}
                  style={{ width: '100%' }}
                >
                  {loading ? 'Signing in...' : 'Sign In as Vendor'}
                </button>
              </div>
            )}

            {error && <p className="login-error-message">{error}</p>}
          </form>

          <div className="login-signup-prompt">
            Not a vendor? <Link to="/signin-type" className="login-signup-link">Go back</Link>
          </div>
        </div>
      </div>

      {/* Vendor Selection Modal */}
      {showVendorSelection && (
        <div className="otp-modal-overlay">
          <div className="otp-modal" style={{ maxWidth: '900px' }}>
            <div className="otp-modal-header">
              <h2>Select Vendor Account</h2>
              <button onClick={closeVendorSelectionModal} className="otp-close-btn" disabled={loading}>
                <FiX size={24} />
              </button>
            </div>
            <div className="otp-modal-body">
              <p className="otp-info-text" style={{ marginBottom: '20px' }}>
                Multiple vendor accounts found. Please select one to continue:
              </p>
              <div style={{ maxHeight: '500px', overflowY: 'auto', padding: '5px' }}>
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(3, 1fr)', 
                  gap: '15px'
                }}>
                  {vendors.map((vendor, index) => (
                    <div
                      key={index}
                      onClick={() => !loading && handleVendorSelect(vendor)}
                      style={{
                        padding: '20px',
                        border: selectedVendor?.vendor_code === vendor.vendor_code ? '2px solid #4a90e2' : '2px solid #e0e0e0',
                        borderRadius: '12px',
                        cursor: loading ? 'not-allowed' : 'pointer',
                        backgroundColor: selectedVendor?.vendor_code === vendor.vendor_code ? '#f0f7ff' : '#fff',
                        transition: 'all 0.3s ease',
                        opacity: loading ? 0.6 : 1,
                        boxShadow: selectedVendor?.vendor_code === vendor.vendor_code ? '0 4px 12px rgba(74, 144, 226, 0.2)' : '0 2px 8px rgba(0,0,0,0.08)',
                        position: 'relative'
                      }}
                      onMouseEnter={(e) => {
                        if (!loading && selectedVendor?.vendor_code !== vendor.vendor_code) {
                          e.currentTarget.style.transform = 'translateY(-4px)';
                          e.currentTarget.style.boxShadow = '0 6px 16px rgba(0,0,0,0.12)';
                          e.currentTarget.style.borderColor = '#4a90e2';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!loading && selectedVendor?.vendor_code !== vendor.vendor_code) {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)';
                          e.currentTarget.style.borderColor = '#e0e0e0';
                        }
                      }}
                    >
                      {selectedVendor?.vendor_code === vendor.vendor_code && (
                        <div style={{
                          position: 'absolute',
                          top: '10px',
                          right: '10px',
                          width: '24px',
                          height: '24px',
                          borderRadius: '50%',
                          backgroundColor: '#4a90e2',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#fff',
                          fontSize: '12px',
                          fontWeight: 'bold'
                        }}>
                          ✓
                        </div>
                      )}
                      
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <FiUser size={16} style={{ color: '#4a90e2', flexShrink: 0 }} />
                          <div style={{ fontSize: '14px', color: '#333', fontWeight: '700' }}>
                            {vendor.vendor_name}
                          </div>
                        </div>
                        
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <FiHash size={16} style={{ color: '#4a90e2', flexShrink: 0 }} />
                          <div style={{ fontSize: '13px', color: '#333', fontWeight: '600' }}>
                            {vendor.vendor_code}
                          </div>
                        </div>
                        
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <FiMail size={16} style={{ color: '#4a90e2', flexShrink: 0 }} />
                          <div style={{ 
                            fontSize: '13px', 
                            color: '#666', 
                            wordBreak: 'break-word',
                            lineHeight: '1.4'
                          }}>
                            {vendor.vendor_mail}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* OTP Verification Modal */}
      {showOtpModal && (
        <div className="otp-modal-overlay">
          <div className="otp-modal">
            <div className="otp-modal-header">
              <h2>Verify OTP</h2>
              <button onClick={closeOtpModal} className="otp-close-btn" disabled={loading}>
                <FiX size={24} />
              </button>
            </div>
            <div className="otp-modal-body">
              <p className="otp-info-text">
                An OTP has been sent to <strong>{userEmail}</strong>
              </p>
              <div className="otp-timer">
                <p className={`otp-timer-text ${timeLeft <= 60 ? 'otp-timer-warning' : ''}`}>
                  Time remaining: <strong>{formatTime(timeLeft)}</strong>
                </p>
              </div>
              <form onSubmit={handleOtpSubmit}>
                <div className="login-input-group">
                  <FiHash className="login-input-icon" />
                  <input
                    type="text"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                    placeholder="Enter 6-digit OTP"
                    className="login-input-field"
                    maxLength="6"
                    disabled={loading}
                    autoFocus
                  />
                </div>
                {otpError && <p className="login-error-message">{otpError}</p>}
                <button
                  type="submit"
                  className="login-button"
                  disabled={loading || otp.length < 6 || timeLeft === 0}
                  style={{ width: '100%', marginTop: '20px' }}
                >
                  {loading ? 'Verifying...' : 'Verify OTP'}
                </button>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VendorLogin;
